import { FC, Fragment, useEffect, useState } from 'react';
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Button, Divider, Grid, Stack } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { SamdSlideImageSection } from '@samd/features/orders/components/slide_image_section';
import { getOrderFields } from '@samd/features/orders/order_detail_helpers';
import { getSamdWorkflowDefinition } from '@samd/workflows';
import type { OrderRepository } from 'src/api/order_repository';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { FieldDisplay } from 'src/components/field_display';
import { Trash } from 'src/components/icons/trash';
import { getRegion } from 'src/config/environment';
import { useWorkflowType } from 'src/distribution_platforms/features/hooks/use_workflow';
import {
  createOrderDetailConfiguration,
  OrderDetailContentProviders,
  OrderDetailDisplayer,
  OrderDetailSection,
} from 'src/distribution_platforms/features/order_details';
import { processFetcherResponse } from 'src/distribution_platforms/features/order_form';
import {
  SamdOrderDetailFieldsFragment,
  SamdOrderListItemFieldsFragment,
  OrderStatus,
  SortBy,
  NStage,
  SpecimenType,
  SurgicalMargin,
} from 'src/types';
import { DistributionPlatform, Region } from 'src/types/env';
import { OrderAction } from 'src/types/loaders';
import type { WorkflowType } from 'src/types/workflows';
import {
  notifyError,
  notifySuccess,
  NStageUtils,
  SpecimenTypeUtils,
  SurgicalMarginUtils,
} from 'src/utils';

/** Minimum age for clinical decision support / combined report downloads. */
const CDS_MIN_PATIENT_AGE = 50;

/**
 * SaMD US Report Ready orders with age >= 50 and N0 get CDS + combined download
 * actions in addition to the test report.
 */
export function canDownloadClinicalDecisionSupportReports(
  order: SamdOrderDetailFieldsFragment,
): boolean {
  return (
    getRegion() === Region.US &&
    order.specimenType === SpecimenType.BreastBiopsy &&
    (order.patientAge ?? 0) >= CDS_MIN_PATIENT_AGE &&
    order.nStage === NStage.N0
  );
}

// Column identifiers for table structure
enum Columns {
  Id = 'ORDER_ID',
  Type = 'TEST_TYPE',
  PatientId = 'PATIENT_ID',
  Date = 'ORDER_DATE',
  Status = 'STATUS',
}

// Show Positive and Negative for Post RP orders.
export const SURGICAL_MARGIN_OPTIONS = [
  SurgicalMargin.Positive,
  SurgicalMargin.Negative,
].map((value) => ({
  value: value,
  name: SurgicalMarginUtils.getSurgicalMarginName(value),
}));

// Only show N0 and N1 for breast orders.
export const N_STAGE_OPTIONS = Object.entries(NStage)
  .filter(([, value]) => [NStage.N0, NStage.N1].includes(value))
  .map(([key, value]) => ({
    value: key,
    name: NStageUtils.getNStageName(value),
  }));

// Create column helper for typed columns (list query uses list-item fragment)
const columnHelper = createColumnHelper<SamdOrderListItemFieldsFragment>();

/** Returns order list column definitions for SaMD (uses region for specimen type column). */
export function getSamdOrderListColumns(workflowType: WorkflowType) {
  const workflow = getSamdWorkflowDefinition(workflowType);
  const hasMultipleSpecimenTypes =
    SpecimenTypeUtils.hasMultipleSpecimenTypesEnabled(
      workflow.supportedSpecimenTypes,
    );

  return [
    columnHelper.accessor('crossOrgId', {
      id: Columns.Id,
      header: 'ORDER ID#',
      size: 130,
      meta: {
        isPrimaryColor: true,
      },
      cell: (info) => info.getValue() || '',
    }),
    ...(hasMultipleSpecimenTypes
      ? [
          columnHelper.accessor('specimenType', {
            id: Columns.Type,
            header: 'ORDER TYPE',
            size: 180,
            cell: (info) =>
              info.getValue()
                ? SpecimenTypeUtils.getSpecimenTypeName(info.getValue())
                : '',
          }),
        ]
      : []),
    columnHelper.accessor('externalId', {
      id: Columns.PatientId,
      header: 'PATIENT ID',
      minSize: 140,
      cell: (info) => info.getValue()?.toString() ?? '',
    }),
    columnHelper.accessor('submittedAt', {
      id: Columns.Date,
      header: 'ORDER DATE',
      size: 145,
      enableSorting: true,
      sortingFn: 'datetime',
      meta: { sortBy: SortBy.SubmittedAt },
      cell: (info) =>
        info.getValue() ? new Date(info.getValue()!).toLocaleDateString() : '',
    }),
    columnHelper.accessor((order) => order.status, {
      id: Columns.Status,
      header: 'STATUS',
      minSize: 180,
      enableSorting: true,
      meta: { sortBy: SortBy.Status },
      cell: (info) => workflow.getStatusDisplayName(info.getValue()),
    }),
  ];
}

const OrderActions: FC<{ order: SamdOrderDetailFieldsFragment }> = ({
  order,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization } = useParams();
  const fetcher = useFetcher();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const handleEditOrderOpen = () => {
    navigate(`/${organization}/orders/${order.id}/edit`, {
      state: {
        background: {
          pathname: location.pathname,
          search: location.search,
        },
      },
    });
  };

  const handleCancelOrder = () => {
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);
    const formData = new FormData();
    formData.append('action', OrderAction.CANCEL);
    formData.append('orderData', JSON.stringify(order));
    fetcher.submit(formData, { method: 'post', action: location.pathname });
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };

  const handleDownloadTestReport = () => {
    const formData = new FormData();
    formData.append('action', OrderAction.DOWNLOAD_REPORT);
    fetcher.submit(formData, { method: 'post', action: location.pathname });
  };

  const handleDownloadClinicalDecisionSupport = () => {
    const formData = new FormData();
    formData.append('action', OrderAction.DOWNLOAD_CLINICAL_DECISION_SUPPORT);
    fetcher.submit(formData, { method: 'post', action: location.pathname });
  };

  const handleDownloadCombinedReport = () => {
    const formData = new FormData();
    formData.append('action', OrderAction.DOWNLOAD_COMBINED_REPORT);
    fetcher.submit(formData, { method: 'post', action: location.pathname });
  };

  // Handle action results
  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state]);

  // Only show Edit and Cancel when status is Lab Processing
  const showEditAndCancel = order.status === OrderStatus.LabProcessing;

  // Show slide image section for Lab Processing, AI Processing, AI Error, and Report Ready statuses
  const showSlideSection = [
    OrderStatus.LabProcessing,
    OrderStatus.AiProcessing,
    OrderStatus.AiError,
    OrderStatus.ReportReady,
  ].includes(order.status);

  // Show download test report for Report Ready status
  const showDownloadReport = order.status === OrderStatus.ReportReady;
  const showCdsDownloads =
    showDownloadReport && canDownloadClinicalDecisionSupportReports(order);

  return (
    <Stack
      spacing={0.5}
      sx={{
        my: -1,
      }}
    >
      {[
        // Edit and Cancel group
        showEditAndCancel && (
          <Stack spacing={0.5} key='edit-cancel'>
            <Button
              size='large'
              sx={{ alignSelf: 'flex-start' }}
              onClick={handleEditOrderOpen}
            >
              EDIT ORDER
            </Button>
            <Divider />
            <Button
              size='large'
              sx={{ alignSelf: 'flex-start' }}
              onClick={handleCancelOrder}
            >
              CANCEL ORDER
            </Button>
          </Stack>
        ),

        showDownloadReport && (
          <Stack spacing={0.5} key='download-reports'>
            {showCdsDownloads && (
              <Stack spacing={1.5}>
                <Button
                  size='medium'
                  variant='contained'
                  color='primary'
                  sx={{ alignSelf: 'flex-start', minWidth: '315px' }}
                  onClick={handleDownloadClinicalDecisionSupport}
                >
                  DOWNLOAD CLINICAL DECISION SUPPORT
                </Button>

                <Divider />
              </Stack>
            )}
            <Button
              size='large'
              sx={{ alignSelf: 'flex-start' }}
              onClick={handleDownloadTestReport}
            >
              DOWNLOAD TEST REPORT
            </Button>
            {showCdsDownloads && (
              <>
                <Divider />
                <Button
                  size='large'
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={handleDownloadCombinedReport}
                >
                  DOWNLOAD ALL
                </Button>
              </>
            )}
          </Stack>
        ),

        showSlideSection && (
          <SamdSlideImageSection key='slides' order={order} />
        ),
      ]
        .filter(Boolean)
        .map((action, index, array) => (
          <Fragment key={`action-${index}`}>
            {action}
            {/* Add divider if this isn't the last item */}
            {index < array.length - 1 && <Divider />}
          </Fragment>
        ))}
      <FeedbackPopup
        data={{
          primaryButtonText: 'YES, CANCEL',
          secondaryButtonText: 'NO',
          title: 'Cancel Order?',
          subtitle:
            'Are you sure you want to cancel this order? This action cannot be undone.',
          type: FeedbackType.Error,
          icon: Trash,
        }}
        isDialogOpen={cancelDialogOpen}
        onPrimaryButtonClick={handleConfirmCancel}
        onSecondaryButtonClick={handleCloseCancelDialog}
        handleDialogClose={handleCloseCancelDialog}
      />
    </Stack>
  );
};

export class SamdProstateOrderDetailDisplayer implements OrderDetailDisplayer {
  constructor(public orderRepository: OrderRepository) {
    const contentProviders: OrderDetailContentProviders = {
      renderInformation: (order) => {
        const OrderInformationFields: FC<{
          order: SamdOrderDetailFieldsFragment;
        }> = ({ order }) => {
          const workflowType = useWorkflowType();
          const orderFields = getOrderFields(order, workflowType, getRegion());

          return (
            <Grid container rowSpacing={3} columnSpacing={3} sx={{ mt: 1 }}>
              {orderFields.map((field) => (
                <Grid key={field.label} size={{ xs: 12, sm: 6 }}>
                  {field.value && (
                    <FieldDisplay
                      field={{
                        label: field.label,
                        value: field.value.toString(),
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          );
        };

        return (
          <OrderInformationFields
            order={order as SamdOrderDetailFieldsFragment}
          />
        );
      },
      renderActions: (order) => (
        <OrderActions order={order as SamdOrderDetailFieldsFragment} />
      ),
    };

    this.sections = createOrderDetailConfiguration(
      DistributionPlatform.Samd,
      contentProviders,
    );
  }

  sections: OrderDetailSection[];
}
